import { Button, ModalSection } from '@monash/portal-react';
import c from './bulk-schedule-modal-contents.module.scss';
import cs from '../bulk-actions-shared.module.scss';
import { useForm } from 'react-hook-form';
import DateAndTime from '../../../../common/date-and-time/DateAndTime';
import ControlledLinkDetailsList from '../controlled-link-details-list/ControlledLinkDetailsList';
import { useWatchCheckedLinksCount } from '../hooks';
import { useContext, useState } from 'react';
import { isSchedulable } from '../../utils/statuses';
import classNames from 'classnames';
import { useAllLinkIdsMap } from '../../hooks/use-all-link-ids-map';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import { APIContext } from '@monash/portal-frontend-common';
import { useLinksState } from '../../hooks/use-links-state';
import { getTimestampFromDateAndTimeInputs } from '../../../../utilities/get-timestamp-from-date-and-time-inputs';
import { getErrorMessage } from '../../../../common/schedule-publish-modal/get-error-message';
import InlineErrorMessage from '../../../../common/inline-error-message/InlineErrorMessage';

const BulkScheduleModalContents = ({
  links,
  onClose,
  updateLink: updateLinkInAllLinks,
  updateChecked,
  ...modalSectionProps
}) => {
  const { updateLink } = useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  // Use a local links state for the modal so that links persist
  // regardless of status changes or top level filters
  const { links: modalLinks, updateLink: updateLinkInModal } = useLinksState(
    () => links.filter(isSchedulable)
  );

  const [hasAttemptedSchedule, setHasAttemptedSchedule] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState({});
  const isSomeLinkLoading = Object.values(isLoadingMap).some(Boolean);
  const [errorMap, setErrorMap] = useState({});
  const [disabledMap, setDisabledMap] = useState({});

  const enrichedModalLinks = modalLinks.map((link) => {
    return {
      ...link,
      isLoading: isLoadingMap[link.id],
      error: errorMap[link.id],
      disabled: disabledMap[link.id],
    };
  });

  const allIdsChecked = useAllLinkIdsMap({
    links: enrichedModalLinks,
    value: true,
  });

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      checkedLinkIds: allIdsChecked,
      startDate: '',
      startTime: '',
    },
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors: formErrors },
    trigger,
  } = methods;

  const checkedLinksCount = useWatchCheckedLinksCount({
    name: 'checkedLinkIds',
    control,
  });

  const attemptSchedule = async (link, startDate) => {
    const { id } = link;

    setIsLoadingMap((prevMap) => {
      return {
        ...prevMap,
        [id]: true,
      };
    });

    const linkWithStartDate = {
      ...link,
      startDate,
    };

    try {
      const updatedLink = await updateLink(linkWithStartDate);
      updateLinkInAllLinks(updatedLink);
      updateLinkInModal(updatedLink);
      setDisabledMap((prevMap) => {
        return {
          ...prevMap,
          [id]: true,
        };
      });
    } catch (error) {
      setErrorMap((prevMap) => {
        return {
          ...prevMap,
          [id]: 'Failed to schedule',
        };
      });

      const errorObj = new Error(error);
      errorObj.id = id;
      throw errorObj; // handle when all promises resolved
    } finally {
      setIsLoadingMap((prevMap) => {
        return {
          ...prevMap,
          [id]: false,
        };
      });
    }
  };

  const submitHandler = async (data) => {
    const { checkedLinkIds, startDate, startTime } = data;

    const resolvedStartDate = getTimestampFromDateAndTimeInputs(
      startDate,
      startTime
    );

    const checkedLinks = enrichedModalLinks.filter((link) => {
      const id = link.id;
      return checkedLinkIds[id];
    });

    const requests = checkedLinks.map((link) => {
      return attemptSchedule(link, resolvedStartDate);
    });

    setHasAttemptedSchedule(true);
    const results = await Promise.allSettled(requests);

    const failedLinkIds = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason.id);

    const failedLinkIdsMap = Object.fromEntries(
      failedLinkIds.map((id) => [id, true])
    );

    updateChecked(failedLinkIdsMap);

    const numberFailed = failedLinkIds.length;

    if (numberFailed > 0) {
      addSnackbar({
        message: `${numberFailed} out of ${results.length} links failed`,
        type: 'error',
      });
    }
  };

  const validateResolvedStartDate = () => {
    const [startDate, startTime] = getValues(['startDate', 'startTime']);

    return getErrorMessage({
      publishDate: startDate,
      publishTime: startTime,
    });
  };

  return (
    <ModalSection
      title={`Schedule ${checkedLinksCount} link${
        checkedLinksCount === 1 ? '' : 's'
      }`}
      noPadding
      onClose={onClose}
      {...modalSectionProps}
    >
      <form onSubmit={handleSubmit(submitHandler)}>
        <ControlledLinkDetailsList
          links={enrichedModalLinks}
          name="checkedLinkIds"
          control={control}
        />
        <div className={classNames(cs.actionInput, c.actionInput)}>
          <DateAndTime
            control={control}
            names={{ date: 'startDate', time: 'startTime' }}
            label="Select date and time"
            validateDate={() => {
              trigger('startTime');
            }}
            validateTime={validateResolvedStartDate}
          />
          {formErrors.startTime && (
            <InlineErrorMessage message={formErrors.startTime.message} />
          )}
          <div className={c.submitButtonContainer}>
            {hasAttemptedSchedule ? (
              <Button
                variant="primary"
                size="medium"
                onClick={onClose}
                loading={isSomeLinkLoading}
              >
                Done
              </Button>
            ) : (
              <Button
                variant="primary"
                size="medium"
                type="submit"
                loading={isSomeLinkLoading}
                disabled={checkedLinksCount === 0}
              >
                Schedule publish
              </Button>
            )}
          </div>
        </div>
      </form>
    </ModalSection>
  );
};

export default BulkScheduleModalContents;
